import { useEffect, useId, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { confirmDiscardIfDirty } from "../components/confirm-discard";
import { GlassDrawer, GlassDrawerFooter } from "../components/GlassDrawer";
import { GlassModal } from "../components/GlassModal";
import {
  archiveRoute,
  createRoute,
  fetchEditableRoutes,
  fetchSites,
  SAFE_ROUTE_WRITE_FAILURE,
  updateRoute,
  type EditableRoute,
  type SiteRow,
} from "../data/routes";
import { useReadModel } from "../data/useReadModel";

// Route editor (CONSOLE-SETUP-ROUTE-EDITOR). The first writable brick of the onboarding
// setup flow: list every route (archived ones visible but labeled - history is never
// hidden), create on a site, edit active routes, archive behind an explicit consequence
// confirm. Writes go ONLY through the hosted route CRUD RPCs; viewers without
// schedule.manage get a read-only list with zero write affordances. Checkpoint management
// (add/reorder/coordinates/tokens) is a separate hosted contract - none of it lives here.

interface FormTarget {
  mode: "create" | "edit";
  route: EditableRoute | null;
}

export function RouteEditor({
  client,
  accountId,
  canManage = false,
  pickerSites,
  onRoutesChanged,
}: {
  client: SupabaseClient;
  accountId: string;
  /** schedule.manage - write affordances only exist when true (fail closed). */
  canManage?: boolean;
  /** Shared site list from Setup (stays in sync when clients/sites change above). */
  pickerSites?: SiteRow[];
  /** Notify parent Setup so Checkpoints picks up new routes without a page refresh. */
  onRoutesChanged?: () => void;
}) {
  const pickerSitesKey = useMemo(
    () => pickerSites?.map((site) => site.siteId).join("|") ?? "",
    [pickerSites],
  );
  const { status, data, error, reload } = useReadModel<{ sites: SiteRow[]; routes: EditableRoute[] }>(
    async () => {
      const routes = await fetchEditableRoutes(client, accountId);
      const sites = pickerSites ?? (await fetchSites(client, accountId));
      return { sites, routes };
    },
    [client, accountId, pickerSites, pickerSitesKey],
  );
  const [formTarget, setFormTarget] = useState<FormTarget | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<EditableRoute | null>(null);
  const [formDirty, setFormDirty] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const [formFailure, setFormFailure] = useState<string | null>(null);

  const drawerDialogKey =
    formTarget !== null
      ? formTarget.mode === "edit"
        ? `edit-route:${formTarget.route!.routeId}`
        : "create-route"
      : null;

  useEffect(() => {
    setFormDirty(false);
    setFormFailure(null);
    setFormBusy(false);
  }, [drawerDialogKey]);

  function clearDrawer(): void {
    setFormTarget(null);
    setFormDirty(false);
    setFormFailure(null);
  }

  function requestDrawerClose(): void {
    if (formBusy) return;
    if (!confirmDiscardIfDirty(formDirty)) return;
    clearDrawer();
  }

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading routes...</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load routes."}
      </p>
    );
  }

  const { sites, routes } = data ?? { sites: [], routes: [] };
  const siteName = (siteId: string): string =>
    sites.find((site) => site.siteId === siteId)?.siteName ?? "Site";
  const hasSites = sites.length > 0;

  return (
    <div className="routes">
      {canManage ? (
        <div className="tpl__actions">
          <button
            type="button"
            className="minter__confirm-go"
            disabled={!hasSites}
            title={hasSites ? undefined : "Create a site under Clients & Sites first"}
            onClick={() => setFormTarget({ mode: "create", route: null })}
          >
            New route
          </button>
        </div>
      ) : null}
      {!hasSites && canManage ? (
        <p className="surface__state setup__empty-site" role="status">
          No sites yet. Add a site under <strong>Clients &amp; Sites</strong> first, then return
          here. New sites appear in this list as soon as they are saved above.
        </p>
      ) : null}
      {routes.length > 0 ? (
        <ul className="routes__list">
          {routes.map((route) => (
            <li
              key={route.routeId}
              className={`routes__item${route.active ? "" : " routes__item--archived"}${
                formTarget?.mode === "edit" && formTarget.route?.routeId === route.routeId
                  ? " routes__item--editing"
                  : ""
              }${archiveTarget?.routeId === route.routeId ? " routes__item--editing" : ""}`}
            >
              <div className="routes__head">
                <span className="routes__name">{route.name}</span>
                {route.active ? null : <span className="schedule__chip schedule__chip--draft">Archived</span>}
                {canManage && route.active ? (
                  <span className="routes__actions">
                    <button
                      type="button"
                      className="builder__navbtn"
                      onClick={() => setFormTarget({ mode: "edit", route })}
                    >
                      Edit
                    </button>
                    <button type="button" className="builder__cancel" onClick={() => setArchiveTarget(route)}>
                      Archive
                    </button>
                  </span>
                ) : null}
              </div>
              <p className="routes__meta">
                {siteName(route.siteId)}
                {route.description ? ` - ${route.description}` : ""}
              </p>
            </li>
          ))}
        </ul>
      ) : hasSites ? (
        <p className="surface__state" role="status">
          No routes yet{canManage ? " - create the first one above" : ""}. Routes group a site's
          checkpoints into a patrol.
        </p>
      ) : null}

      {canManage && formTarget !== null ? (
        <GlassDrawer
          open
          busy={formBusy}
          dirty={formDirty}
          ariaLabel={
            formTarget.mode === "create" ? "New route" : `Edit route - ${formTarget.route?.name ?? ""}`
          }
          onClose={clearDrawer}
        >
          <RouteForm
            key={formTarget.mode === "edit" ? formTarget.route!.routeId : "create-route"}
            client={client}
            accountId={accountId}
            sites={sites}
            target={formTarget}
            busy={formBusy}
            failure={formFailure}
            onBusyChange={setFormBusy}
            onFailureChange={setFormFailure}
            onDirtyChange={setFormDirty}
            onClose={requestDrawerClose}
            onSaved={() => {
              clearDrawer();
              reload();
              onRoutesChanged?.();
            }}
          />
        </GlassDrawer>
      ) : null}

      {canManage && archiveTarget !== null ? (
        <GlassModal
          open
          ariaLabel={`Archive route - ${archiveTarget.name}`}
          onClose={() => setArchiveTarget(null)}
        >
          <ArchiveDialog
            client={client}
            accountId={accountId}
            route={archiveTarget}
            onClose={() => setArchiveTarget(null)}
            onArchived={() => {
              setArchiveTarget(null);
              reload();
              onRoutesChanged?.();
            }}
          />
        </GlassModal>
      ) : null}
    </div>
  );
}

/** Explicit-submit create/edit form. The server refuses archived-route edits regardless. */
function RouteForm({
  client,
  accountId,
  sites,
  target,
  busy,
  failure,
  onBusyChange,
  onFailureChange,
  onClose,
  onSaved,
  onDirtyChange,
}: {
  client: SupabaseClient;
  accountId: string;
  sites: SiteRow[];
  target: FormTarget;
  busy: boolean;
  failure: string | null;
  onBusyChange: (busy: boolean) => void;
  onFailureChange: (failure: string | null) => void;
  onClose: () => void;
  onSaved: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const initialSiteId = target.route?.siteId ?? "";
  const initialName = target.route?.name ?? "";
  const initialDescription = target.route?.description ?? "";
  const [siteId, setSiteId] = useState(initialSiteId);
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const hasSites = sites.length > 0;
  // The action row renders on the drawer's panel edge, outside this form's DOM subtree,
  // so its submit button carries `form=` to stay bound to this form.
  const formId = useId();

  const dirty =
    siteId !== initialSiteId || name !== initialName || description !== initialDescription;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  async function submit(): Promise<void> {
    if (busy) return;
    onBusyChange(true);
    onFailureChange(null);
    try {
      const desc = description.trim() === "" ? null : description.trim();
      const outcome =
        target.mode === "create"
          ? await createRoute(client, accountId, siteId, name.trim(), desc)
          : await updateRoute(client, accountId, target.route!.routeId, siteId, name.trim(), desc);
      if (outcome.ok) {
        onSaved();
      } else {
        onFailureChange(outcome.reason);
      }
    } catch {
      onFailureChange(SAFE_ROUTE_WRITE_FAILURE);
    } finally {
      onBusyChange(false);
    }
  }

  return (
    <form
      id={formId}
      className="minter__confirm tpl__form"
      aria-label={target.mode === "create" ? "New route" : `Edit route - ${target.route?.name ?? ""}`}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <h3 className="minter__confirm-title">
        {target.mode === "create" ? "New route" : `Edit route - ${target.route?.name ?? ""}`}
      </h3>
      {hasSites ? (
        <label className="tpl__field">
          Site
          <select value={siteId} onChange={(event) => setSiteId(event.target.value)} required>
            <option value="">Choose a site...</option>
            {sites.map((site) => (
              <option key={site.siteId} value={site.siteId}>
                {site.siteName}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="minter__confirm-warning" role="alert">
          No sites available. Add a site under Clients &amp; Sites first, then return here to create
          a route on that site.
        </p>
      )}
      <label className="tpl__field">
        Name
        <input type="text" value={name} onChange={(event) => setName(event.target.value)} required />
      </label>
      <label className="tpl__field">
        Description (optional)
        <input type="text" value={description} onChange={(event) => setDescription(event.target.value)} />
      </label>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      <GlassDrawerFooter>
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="submit" form={formId} className="minter__confirm-go" disabled={busy || !hasSites}>
          {target.mode === "create" ? "Create route" : "Save route"}
        </button>
      </GlassDrawerFooter>
    </form>
  );
}

/** Archive = retire, never delete. The consequence copy says exactly that. */
function ArchiveDialog({
  client,
  accountId,
  route,
  onClose,
  onArchived,
}: {
  client: SupabaseClient;
  accountId: string;
  route: EditableRoute;
  onClose: () => void;
  onArchived: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function confirm(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const outcome = await archiveRoute(client, accountId, route.routeId);
      if (outcome.ok) {
        onArchived();
      } else {
        setFailure(outcome.reason);
      }
    } catch {
      setFailure(SAFE_ROUTE_WRITE_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="minter__confirm" aria-label={`Archive route - ${route.name}`}>
      <h3 className="minter__confirm-title">Archive route - {route.name}</h3>
      <p className="minter__confirm-warning">
        The route leaves pickers and scheduling, but its history - scans, shifts, audit -
        stays preserved. Archived routes cannot be edited.
      </p>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="minter__confirm-go" onClick={() => void confirm()} disabled={busy}>
          Confirm archive
        </button>
      </div>
    </div>
  );
}
